import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError, setToken } from "@/lib/api";
import Logo from "@/components/Logo";

const HERO = "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=1600&q=80";

export default function ClientJoinPage() {
    const { code } = useParams();
    const [params] = useSearchParams();
    const via = params.get("via") || null;
    const nav = useNavigate();
    const [coach, setCoach] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", password: "", goal: "", phone: "" });

    useEffect(() => {
        if (!code) return;
        api.get(`/coach/by-code/${code}`)
            .then(({ data }) => setCoach(data))
            .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Coach not found"));
    }, [code]);

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            const { data } = await api.post("/clients/join", { ...form, coach_code: code, via });
            setToken(data.token);
            window.location.assign("/client");
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    if (!code) return <div className="min-h-screen flex items-center justify-center text-[var(--text-muted)] bg-[var(--bg)]">No coach code provided.</div>;

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[var(--bg)]" data-testid="client-join-page">
            {/* Hero left */}
            <div className="relative hidden lg:block overflow-hidden border-r border-[var(--border)]">
                <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
                <div className="absolute inset-0 bg-gradient-to-br from-[#0a0908] via-[#0a0908]/85 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-between p-14">
                    <Logo size="md" subtitle />
                    <div>
                        <p className="font-mono text-[10px] tracking-[0.4em] text-[var(--gold)] mb-6">/ ATHLETE PORTAL</p>
                        <h1 className="font-serif text-7xl xl:text-8xl leading-[0.95] mb-7">
                            Train under<br />
                            <span className="gold-gradient-text italic">{coach?.coach_name || "your coach"}.</span>
                        </h1>
                        <div className="gold-rule mb-7" />
                        <p className="text-[var(--text-muted)] max-w-md leading-relaxed text-[15px]">
                            Programs designed for you. Daily habits that stack the wins. Weekly check-ins your coach actually reads. Welcome to NDE Fitness.
                        </p>
                    </div>
                    <div className="flex gap-10 font-mono text-[10px] tracking-[0.3em] text-[var(--text-muted)]">
                        <div><span className="text-[var(--gold)]">01</span> · WORKOUTS</div>
                        <div><span className="text-[var(--gold)]">02</span> · HABITS</div>
                        <div><span className="text-[var(--gold)]">03</span> · PROGRESS</div>
                    </div>
                </div>
            </div>

            {/* Form right */}
            <div className="flex items-center justify-center p-6 lg:p-14">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-10"><Logo size="sm" /></div>
                    {!coach && !err && <p className="text-[var(--text-muted)] text-sm">Looking up your coach…</p>}
                    {err && !coach && (
                        <div className="border border-[var(--danger)] bg-[var(--danger)]/10 text-[var(--danger)] p-4" data-testid="join-error-coach">
                            {err}
                        </div>
                    )}
                    {coach && (
                        <>
                            <div className="border border-[var(--gold-deep)] bg-[var(--surface)] p-4 mb-8 flex items-center justify-between">
                                <div>
                                    <p className="label-tiny mb-1 text-[var(--gold-bright)]">Joining</p>
                                    <p className="font-serif text-2xl">{coach.coach_name}</p>
                                </div>
                                <span className="tag-pill border-[var(--gold)] text-[var(--gold-bright)] whitespace-nowrap">{via ? "REFERRED" : "ATHLETE"}</span>
                            </div>
                            <p className="label-tiny mb-3">/ Create your athlete account</p>
                            <h2 className="font-serif text-5xl mb-8">Let's get to work.</h2>
                            <form onSubmit={submit} className="space-y-5" data-testid="join-form">
                                <div>
                                    <label className="label-tiny block mb-2">Full name</label>
                                    <input required className="input-dark" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="join-name" />
                                </div>
                                <div>
                                    <label className="label-tiny block mb-2">Email</label>
                                    <input required type="email" className="input-dark" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="join-email" />
                                </div>
                                <div>
                                    <label className="label-tiny block mb-2">Password</label>
                                    <input required type="password" minLength={6} className="input-dark" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="join-password" />
                                </div>
                                <div>
                                    <label className="label-tiny block mb-2">Your main goal (optional)</label>
                                    <input className="input-dark" placeholder="e.g. drop 10lbs · build muscle · run a half" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} data-testid="join-goal" />
                                </div>
                                {err && <div className="text-sm text-[var(--danger)]" data-testid="join-error">{err}</div>}
                                <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="join-submit">
                                    {busy ? "Creating…" : "Join & enter portal →"}
                                </button>
                            </form>
                            <div className="gold-rule my-10" />
                            <p className="text-sm text-[var(--text-muted)]">Already have an account? <Link to="/login" className="text-[var(--gold)]">Sign in</Link></p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
